import React from 'react'

export type EmojiKey =
    // Priority emojis
    | 'priority:critical'
    | 'priority:high'
    | 'priority:medium'
    | 'priority:low'
    | 'priority:default'
    // Type emojis
    | 'type:nda'
    | 'type:quote_request'
    | 'type:bom_approval'
    | 'type:term_negotiation'
    | 'type:default'
    // Stage emojis
    | 'stage:initial_contact'
    | 'stage:requirement_gathering'
    | 'stage:technical_evaluation'
    | 'stage:commercial_discussion'
    | 'stage:default'
    // Status emojis
    | 'status:draft'
    | 'status:in_progress'
    | 'status:pending_review'
    | 'status:waiting_approval'
    | 'status:approved'
    | 'status:completed'
    | 'status:rejected'
    | 'status:cancelled'
    | 'status:default'
    | 'status:paused'
    // Journey stage emojis
    | 'journey:lead'
    | 'journey:qualified'
    | 'journey:proposal'
    | 'journey:negotiation'
    | 'journey:closed_won'
    | 'journey:closed_lost'
    | 'journey:default'
    // General purpose emojis
    | 'general:success'
    | 'general:warning'
    | 'general:error'
    | 'general:info'
    | 'general:loading'
    | 'general:money'
    | 'general:time'
    | 'general:team'
    | 'general:progress'
    | 'general:target'
    | 'general:magic'
    | 'general:heart'
    | 'general:fire'
    | 'general:star'
    | 'general:default'
    | 'general:computer'
    | 'general:balance'
    | 'general:wrench'
    | 'general:empty'
    | 'general:vm-instance'
    | 'general:vm-lb'
    | 'general:vm-other'
    // ticket status
    | 'ticket:open'
    | 'ticket:doing'
    | 'ticket:blocked'
    | 'ticket:rejected'
    | 'ticket:done'
    | 'ticket:todo'

export interface EmojiMapping {
    emoji: string
    animation?: 'bounce' | 'pulse' | 'spin' | 'bounce-slow' | 'float' | 'none'
    className?: string
}

const EMOJI_MAP: Record<EmojiKey, EmojiMapping> = {
    // Priority mappings
    'priority:critical': { emoji: '🔥', animation: 'pulse' },
    'priority:high': { emoji: '⚡', animation: 'bounce' },
    'priority:medium': { emoji: '⭐', animation: 'none' },
    'priority:low': { emoji: '🔹', animation: 'none' },
    'priority:default': { emoji: '📋', animation: 'none' },

    // Type mappings
    'type:nda': { emoji: '🔒', animation: 'none' },
    'type:quote_request': { emoji: '💰', animation: 'bounce' },
    'type:bom_approval': { emoji: '✅', animation: 'none' },
    'type:term_negotiation': { emoji: '📝', animation: 'none' },
    'type:default': { emoji: '📄', animation: 'none' },

    // Stage mappings
    'stage:initial_contact': { emoji: '👋', animation: 'bounce' },
    'stage:requirement_gathering': { emoji: '📋', animation: 'none' },
    'stage:technical_evaluation': { emoji: '🔍', animation: 'none' },
    'stage:commercial_discussion': { emoji: '💼', animation: 'none' },
    'stage:default': { emoji: '🎯', animation: 'none' },

    // Status mappings
    'status:draft': { emoji: '📝', animation: 'none' },
    'status:in_progress': { emoji: '⚙️', animation: 'spin' },
    'status:pending_review': { emoji: '👀', animation: 'pulse' },
    'status:waiting_approval': { emoji: '⏳', animation: 'pulse' },
    'status:approved': { emoji: '✅', animation: 'bounce' },
    'status:completed': { emoji: '🎉', animation: 'bounce' },
    'status:rejected': { emoji: '❌', animation: 'none' },
    'status:cancelled': { emoji: '🚫', animation: 'none' },
    'status:default': { emoji: '📊', animation: 'none' },
    'status:paused': { emoji: '⏸️', animation: 'none' },

    // Journey stage mappings
    'journey:lead': { emoji: '🌱', animation: 'bounce-slow' },
    'journey:qualified': { emoji: '🎯', animation: 'pulse' },
    'journey:proposal': { emoji: '📄', animation: 'none' },
    'journey:negotiation': { emoji: '🤝', animation: 'bounce' },
    'journey:closed_won': { emoji: '🏆', animation: 'bounce' },
    'journey:closed_lost': { emoji: '💔', animation: 'none' },
    'journey:default': { emoji: '🚀', animation: 'none' },

    // General purpose mappings
    'general:success': { emoji: '✅', animation: 'bounce' },
    'general:warning': { emoji: '⚠️', animation: 'pulse' },
    'general:error': { emoji: '❌', animation: 'none' },
    'general:info': { emoji: 'ℹ️', animation: 'none' },
    'general:loading': { emoji: '⏳', animation: 'spin' },
    'general:money': { emoji: '💎', animation: 'bounce' },
    'general:time': { emoji: '⏰', animation: 'pulse' },
    'general:team': { emoji: '👥', animation: 'none' },
    'general:progress': { emoji: '📊', animation: 'none' },
    'general:target': { emoji: '🎯', animation: 'none' },
    'general:magic': { emoji: '✨', animation: 'bounce' },
    'general:heart': { emoji: '💖', animation: 'pulse' },
    'general:fire': { emoji: '🔥', animation: 'pulse' },
    'general:star': { emoji: '⭐', animation: 'bounce-slow' },
    'general:default': { emoji: '📍', animation: 'none' },
    'general:computer': { emoji: '🖥️', animation: 'none' },
    'general:balance': { emoji: '⚖️', animation: 'none' },
    'general:wrench': { emoji: '🔧', animation: 'none' },
    'general:empty': { emoji: '📭', animation: 'none' },
    'general:vm-instance': { emoji: '🖥️', animation: 'none' },
    'general:vm-lb': { emoji: '⚖️', animation: 'none' },
    'general:vm-other': { emoji: '🔧', animation: 'none' },
    // ticket status
    'ticket:open': { emoji: '🔍', animation: 'pulse' },
    'ticket:doing': { emoji: '⏳', animation: 'pulse' },
    'ticket:blocked': { emoji: '⚠️', animation: 'pulse' },
    'ticket:rejected': { emoji: '❌', animation: 'none' },
    'ticket:done': { emoji: '✅', animation: 'bounce' },
    'ticket:todo': { emoji: '❓', animation: 'none' },
}

export interface EmojiProps {
    emojiKey: EmojiKey
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    className?: string
    onClick?: () => void
    disabled?: boolean
    customAnimation?:
        | 'bounce'
        | 'pulse'
        | 'spin'
        | 'bounce-slow'
        | 'float'
        | 'none'
    title?: string
}

const Emoji: React.FC<EmojiProps> = ({
    emojiKey,
    size = 'md',
    className = '',
    onClick,
    disabled = false,
    customAnimation,
    title,
}) => {
    const mapping = EMOJI_MAP[emojiKey] || EMOJI_MAP['general:default']
    const animation = customAnimation || mapping.animation || 'none'

    const sizeClasses = {
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg',
        xl: 'text-xl',
        '2xl': 'text-2xl',
    }

    const animationClasses = {
        bounce: 'animate-bounce',
        pulse: 'animate-pulse',
        spin: 'animate-spin',
        'bounce-slow': 'animate-bounce-slow',
        float: 'animate-float',
        none: '',
    }

    const baseClasses = [
        'inline-block',
        'transition-all',
        'duration-200',
        sizeClasses[size],
        animationClasses[animation],
        mapping.className || '',
        className,
    ]
        .filter(Boolean)
        .join(' ')

    const interactiveClasses =
        onClick && !disabled
            ? 'cursor-pointer hover:scale-110 active:scale-95'
            : ''

    const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : ''

    const finalClasses = [baseClasses, interactiveClasses, disabledClasses]
        .filter(Boolean)
        .join(' ')

    return (
        <span
            className={finalClasses}
            onClick={onClick && !disabled ? onClick : undefined}
            title={title}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick && !disabled ? 0 : undefined}
            onKeyDown={
                onClick && !disabled
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              onClick()
                          }
                      }
                    : undefined
            }
        >
            {mapping.emoji}
        </span>
    )
}

// Helper functions for easy emoji access
export const getEmojiByKey = (key: EmojiKey): string => {
    return EMOJI_MAP[key]?.emoji || EMOJI_MAP['general:default'].emoji
}

export const createEmojiKey = (category: string, value: string): EmojiKey => {
    const key = `${category}:${value}` as EmojiKey
    return EMOJI_MAP[key] ? key : (`${category}:default` as EmojiKey)
}

// Legacy helper functions for backward compatibility
export const getPriorityEmoji = (priority: string): string => {
    return getEmojiByKey(createEmojiKey('priority', priority))
}

export const getTypeEmoji = (type: string): string => {
    return getEmojiByKey(createEmojiKey('type', type))
}

export const getStageEmoji = (stage: string): string => {
    return getEmojiByKey(createEmojiKey('stage', stage))
}

export const getStatusEmoji = (status: string): string => {
    return getEmojiByKey(createEmojiKey('status', status))
}

export const getJourneyEmoji = (journey: string): string => {
    return getEmojiByKey(createEmojiKey('journey', journey))
}

export default Emoji
